import { DefaultPaymentMethodInvoice, ProviderDefaultPaymentMethodResult } from "@/redux/apis/payment/stripe/types";
import { formatCurrency } from "@/utils";

export interface PaymentMethodResultFeedback {
  /** Short confirmation; only when the whole change went through */
  toast?: { type: "success" | "info"; message: string };
  /** Shown in the modal when the overdue payment needs the operator's attention */
  alert?: {
    type: "warning" | "info";
    message: string;
    link?: { label: string; href: string };
    /** Offer to re-apply the same card (the backend call is idempotent) */
    retry?: boolean;
  };
}

/** Stripe decline codes an operator is likely to see, in plain words. */
const DECLINE_REASONS: Record<string, string> = {
  insufficient_funds: "insufficient funds",
  do_not_honor: "your bank declined it",
  generic_decline: "your bank declined it",
  card_velocity_exceeded: "too many attempts on the card",
  expired_card: "the card has expired",
  incorrect_cvc: "incorrect security code",
  incorrect_number: "incorrect card number",
  lost_card: "the card was reported lost",
  stolen_card: "the card was reported stolen",
  processing_error: "a processing error at your bank",
  call_issuer: "your bank asked to be contacted",
  transaction_not_allowed: "the card does not allow this payment",
};

const describeDecline = (code: string): string => DECLINE_REASONS[code] ?? code.replace(/_/g, " ");

/** "your overdue invoice of $449.00" / "your overdue invoices totalling $898.00" */
const invoicePhrase = (invoices: DefaultPaymentMethodInvoice[]): string => {
  if (invoices.length === 0) return "your overdue invoice";
  const total = invoices.reduce((sum, invoice) => sum + invoice.amountDue, 0);
  const amount = formatCurrency(total, 2, invoices[0].currency.toUpperCase());
  return invoices.length > 1 ? `your overdue invoices totalling ${amount}` : `your overdue invoice of ${amount}`;
};

const byOutcome = (result: ProviderDefaultPaymentMethodResult, outcomes: DefaultPaymentMethodInvoice["outcome"][]) =>
  result.outstandingInvoices.filter((invoice) => outcomes.includes(invoice.outcome));

const SUPPORT_LINE = "If your account stays overdue, please contact support.";

/**
 * What to tell the operator after a card was set as default. The backend also
 * pays any overdue renewal invoice with the new card, and a declined or
 * 3DS-blocked payment comes back as a result, not an error: without this the
 * operator would see a green "updated" toast while their account stays overdue.
 */
export const describePaymentMethodResult = (
  result: ProviderDefaultPaymentMethodResult,
  options: { addedCard: boolean }
): PaymentMethodResultFeedback => {
  const base = options.addedCard ? "Payment method added and set as default" : "Default payment method updated";
  const declined = byOutcome(result, ["declined"]);
  const needsAction = byOutcome(result, ["requires_action"]);
  const hostedInvoiceUrl = needsAction.find((invoice) => invoice.hostedInvoiceUrl)?.hostedInvoiceUrl;
  const declineCode = declined.find((invoice) => invoice.declineCode)?.declineCode;

  switch (result.summary) {
    case "no_outstanding_balance":
      return { toast: { type: "success", message: base } };

    case "outstanding_paid":
      return {
        toast: {
          type: "success",
          message: `${base}. ${capitalize(invoicePhrase(byOutcome(result, ["paid", "already_paid"])))} has been paid.`,
        },
      };

    case "outstanding_declined":
      return {
        alert: {
          type: "warning",
          message:
            `Your card is now the default, but the payment for ${invoicePhrase(declined)} was declined` +
            `${declineCode ? ` (${describeDecline(declineCode)})` : ""}. Please try another card or contact your bank.`,
        },
      };

    case "action_required":
      return {
        alert: {
          type: "warning",
          message: `Your card is now the default. Your bank needs you to confirm the payment for ${invoicePhrase(needsAction)}.`,
          ...(hostedInvoiceUrl ? { link: { label: "Confirm the payment on Stripe", href: hostedInvoiceUrl } } : {}),
        },
      };

    case "in_progress":
      return {
        alert: {
          type: "info",
          message:
            "Your card is now the default. The payment for your overdue invoice is still being processed; we'll email you once it goes through, or you can try again in a few minutes.",
          retry: true,
        },
      };

    case "partial":
    default: {
      // Mixed outcomes, or the card could not be applied to every subscription:
      // keep whatever is actionable (decline reason, 3DS link) instead of a
      // generic line.
      const parts: string[] = ["Your card was saved, but it could not be applied to everything on your account."];
      if (declined.length > 0) {
        parts.push(`The payment for ${invoicePhrase(declined)} was declined${declineCode ? ` (${describeDecline(declineCode)})` : ""}.`);
      }
      if (needsAction.length > 0) {
        parts.push(`Your bank needs you to confirm the payment for ${invoicePhrase(needsAction)}.`);
      }
      parts.push(SUPPORT_LINE);
      return {
        alert: {
          type: "warning",
          message: parts.join(" "),
          ...(hostedInvoiceUrl ? { link: { label: "Confirm the payment on Stripe", href: hostedInvoiceUrl } } : {}),
          retry: true,
        },
      };
    }
  }
};

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);
